import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import TextInput from "../../common/hookForm/TextInput";
import PrimaryButton, { PrimaryButtonType } from "../../common/PrimaryButton";
import styles from "../styles.module.scss";
import {
  CR_NUMBER_LENGTH,
  CR_NUMBER_PATTERN,
  IBAN_PATTERN,
  MAX_BENEFICIARY_NAME_LENGTH,
  MIN_BENEFICIARY_NAME_LENGTH,
  UNIFIED_NUMBER_LENGTH,
  UNIFIED_NUMBER_PATTERN,
  formatIban,
  normalizeIban,
} from "../organizationRegistration.constants";
import ActivationTaskModal from "./ActivationTaskModal";
import { IBankAccountDraft } from "./types";

// TextInput requires onBlur; these fields have no blur behaviour.
const noop = () => undefined;

interface Props {
  visible: boolean;
  /** Previously saved values, when the user reopens the task to edit it. */
  initialValue: IBankAccountDraft | null;
  onSave: (value: IBankAccountDraft) => void;
  onClose: () => void;
}

/**
 * Activation task: the organization's IBAN, account holder name, commercial register number and
 * unified number — the backend validates the IBAN against the CR / unified number.
 */
const BankAccountModal: React.FC<Props> = ({
  visible,
  initialValue,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const [iban, setIban] = useState("");
  const [beneficiaryName, setBeneficiaryName] = useState("");
  const [commercialRegisterNumber, setCommercialRegisterNumber] = useState("");
  const [unifiedNumber, setUnifiedNumber] = useState("");

  // Every opening starts from what was last saved, so cancelling discards edits.
  useEffect(() => {
    if (!visible) return;
    setIban(formatIban(initialValue?.iban ?? ""));
    setBeneficiaryName(initialValue?.beneficiaryName ?? "");
    setCommercialRegisterNumber(initialValue?.commercialRegisterNumber ?? "");
    setUnifiedNumber(initialValue?.unifiedNumber ?? "");
  }, [visible, initialValue]);

  const normalizedIban = normalizeIban(iban);

  // The input only accepts digits, so a CR number is valid once all 10 are typed.
  const isCrNumberValid = CR_NUMBER_PATTERN.test(commercialRegisterNumber);
  const isUnifiedNumberValid = UNIFIED_NUMBER_PATTERN.test(unifiedNumber);
  // Flag it only once fully typed — a partial number is just "not done yet".
  const unifiedNumberError =
    unifiedNumber.length === UNIFIED_NUMBER_LENGTH && !isUnifiedNumberValid
      ? (t("lbl_org_act_unified_number_invalid") as string)
      : undefined;

  const canSave =
    IBAN_PATTERN.test(normalizedIban) &&
    beneficiaryName.trim().length >= MIN_BENEFICIARY_NAME_LENGTH &&
    isCrNumberValid &&
    isUnifiedNumberValid;

  const onSubmit = () => {
    if (!canSave) return;
    onSave({
      iban: normalizedIban,
      beneficiaryName: beneficiaryName.trim(),
      commercialRegisterNumber,
      unifiedNumber,
    });
  };

  return (
    <ActivationTaskModal
      visible={visible}
      onClose={onClose}
      title={t("lbl_org_act_bank_title")}
      subtitle={t("lbl_org_act_bank_subtitle") as string}
      actions={
        <>
          <PrimaryButton type="button" disabled={!canSave} onClick={onSubmit}>
            {t("lbl_org_act_save")}
          </PrimaryButton>
          <PrimaryButton
            type="button"
            buttonType={PrimaryButtonType.PRIMARY_LIGHT}
            onClick={onClose}>
            {t("lbl_org_act_cancel")}
          </PrimaryButton>
        </>
      }>
      <div className={styles.tmField}>
        <TextInput
          name="iban"
          dir="ltr"
          value={iban}
          onChange={(e) => setIban(formatIban(e.target.value))}
          onBlur={noop}
          placeholder={t("lbl_org_field_iban") as string}
          hint="SA44 2000 0000 0123 4567 8912"
        />
      </div>
      <div className={styles.tmField}>
        <TextInput
          name="beneficiaryName"
          value={beneficiaryName}
          onChange={(e) =>
            setBeneficiaryName(
              e.target.value.slice(0, MAX_BENEFICIARY_NAME_LENGTH)
            )
          }
          onBlur={noop}
          placeholder={t("lbl_org_act_beneficiary_name") as string}
          hint={t("lbl_org_act_beneficiary_hint") as string}
        />
      </div>
      <div className={styles.tmField}>
        <TextInput
          name="commercialRegisterNumber"
          dir="ltr"
          inputType="number"
          disableDecimal
          value={commercialRegisterNumber}
          onChange={(e) =>
            setCommercialRegisterNumber(
              e.target.value.replace(/\D/g, "").slice(0, CR_NUMBER_LENGTH)
            )
          }
          onBlur={noop}
          placeholder={t("lbl_org_field_cr_number") as string}
          hint={t("lbl_org_act_cr_number_hint") as string}
        />
      </div>
      <div className={styles.tmField}>
        <TextInput
          name="unifiedNumber"
          dir="ltr"
          inputType="number"
          disableDecimal
          value={unifiedNumber}
          onChange={(e) =>
            setUnifiedNumber(
              e.target.value.replace(/\D/g, "").slice(0, UNIFIED_NUMBER_LENGTH)
            )
          }
          onBlur={noop}
          error={unifiedNumberError}
          placeholder={t("lbl_org_field_unified_number") as string}
          hint={
            unifiedNumberError
              ? undefined
              : (t("lbl_org_act_unified_number_hint") as string)
          }
        />
      </div>
    </ActivationTaskModal>
  );
};

export default BankAccountModal;
